import { createRootRoute, createRoute, createRouter, Link, Navigate, Outlet } from '@tanstack/react-router';
import { Button, EmptyState } from '@tatra/ui';
import type { ReactNode } from 'react';
import { Loading } from './components.js';
import { useSession } from './lib/session.js';
import { ProfilePage, TeamPage } from './pages/account.js';
import { ApplyPage, ForgotPage, SignInPage, TokenPasswordPage } from './pages/auth.js';
import { HomePage } from './pages/home.js';
import { LeadDetailPage, LeadsPage, RegisterLeadPage } from './pages/leads.js';
import { ClaimDetailPage, ClaimsPage, CommissionsPage } from './pages/money.js';
import { Shell } from './shell.js';

function NotFound() {
  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <EmptyState
        title="That page does not exist"
        action={
          <Link to="/">
            <Button>Go home</Button>
          </Link>
        }
      />
    </div>
  );
}

const root = createRootRoute({ component: Outlet, notFoundComponent: NotFound });

/** Signed-out screens send a signed-in partner to the home page. */
function Public({ children }: { children: ReactNode }) {
  const { state } = useSession();
  if (state === 'loading') return <Loading />;
  if (state === 'in') return <Navigate to="/" />;
  return children;
}

const token = (s: Record<string, unknown>) => ({ token: typeof s['token'] === 'string' ? s['token'] : '' });

const app = createRoute({ getParentRoute: () => root, id: 'app', component: Shell });

const signIn = createRoute({
  getParentRoute: () => root,
  path: '/sign-in',
  component: () => (
    <Public>
      <SignInPage />
    </Public>
  ),
});
const apply = createRoute({
  getParentRoute: () => root,
  path: '/apply',
  component: () => (
    <Public>
      <ApplyPage />
    </Public>
  ),
});
const forgot = createRoute({
  getParentRoute: () => root,
  path: '/forgot',
  component: () => (
    <Public>
      <ForgotPage />
    </Public>
  ),
});
const accept = createRoute({
  getParentRoute: () => root,
  path: '/accept',
  validateSearch: token,
  component: () => (
    <Public>
      <TokenPasswordPage kind="accept" />
    </Public>
  ),
});
const reset = createRoute({
  getParentRoute: () => root,
  path: '/reset',
  validateSearch: token,
  component: () => (
    <Public>
      <TokenPasswordPage kind="reset" />
    </Public>
  ),
});

const home = createRoute({ getParentRoute: () => app, path: '/', component: HomePage });
const register = createRoute({ getParentRoute: () => app, path: '/register', component: RegisterLeadPage });
const leads = createRoute({ getParentRoute: () => app, path: '/leads', component: LeadsPage });
const lead = createRoute({ getParentRoute: () => app, path: '/leads/$id', component: LeadDetailPage });
const commissions = createRoute({
  getParentRoute: () => app,
  path: '/commissions',
  component: CommissionsPage,
});
const claims = createRoute({ getParentRoute: () => app, path: '/claims', component: ClaimsPage });
const claim = createRoute({ getParentRoute: () => app, path: '/claims/$id', component: ClaimDetailPage });
const team = createRoute({ getParentRoute: () => app, path: '/team', component: TeamPage });
const profile = createRoute({ getParentRoute: () => app, path: '/profile', component: ProfilePage });

const routeTree = root.addChildren([
  signIn,
  apply,
  forgot,
  accept,
  reset,
  app.addChildren([home, register, leads, lead, commissions, claims, claim, team, profile]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
